@if (isVisible) {
  <div class="theme-playground" aria-label="Theme Playground">
    <!-- Toggle Button (Always visible) -->
    <div class="style-toggle">
      <button class="toggle-btn" (click)="toggleExpanded()" type="button">
        {{ isExpanded ? 'Hide Builder' : 'Show Builder' }}
      </button>
    </div>

    <!-- Expandable Content -->
    @if (isExpanded) {
      <div class="playground-content">
        <!-- Mode Selector -->
        <div class="style-toggle">
          <label for="modeSelect">Editing Mode</label>
          <select
            id="modeSelect"
            [value]="themeBuilder.currentMode()"
            (change)="onModeChange($event)"
            aria-label="Theme mode">
            <option value="light">Light</option>
            <option value="dark">Dark</option>
          </select>
        </div>

        <!-- Filter Toggle -->
        <div class="style-toggle">
          <label for="filterToggle">Show Variables</label>
          <select
            id="filterToggle"
            [value]="showActiveOnly ? 'active' : 'all'"
            (change)="toggleActiveFilter()"
            aria-label="Filter variables">
            <option value="all">All Variables</option>
            <option value="active">Active Only</option>
          </select>
        </div>

        <!-- Variable List -->
        @for (varName of getSortedVariables(); track varName) {
          @if (!isMetadataField(varName)) {
            <div class="style-toggle" [class.inactive]="!isActiveVariable(varName)">
              <label [for]="'var-' + varName">
                {{ getVariableLabel(varName) }}
              </label>

              <!-- Color fields (color picker) -->
              @if (getFieldType(varName) === 'color') {
                <div class="color-input-group">
                  <input
                    type="color"
                    [value]="getColorPickerValue(varName)"
                    (input)="onColorPickerChange(varName, $event)"
                    (focus)="onFieldFocus(varName)"
                    (blur)="onFieldBlur()"
                    class="color-picker-input"
                    [title]="'Pick color for ' + varName"
                  />
                  <input
                    [id]="'var-' + varName"
                    type="text"
                    [value]="getVariableValue(varName)"
                    (input)="onVariableChange(varName, $event)"
                    (focus)="onFieldFocus(varName)"
                    (blur)="onFieldBlur()"
                    placeholder="Enter color or use picker"
                    autocomplete="off"
                    spellcheck="false"
                    class="color-text-input"
                  />
                </div>
              } @else if (getFieldType(varName) === 'dropdown') {
                <select
                  [id]="'var-' + varName"
                  [value]="getVariableValue(varName)"
                  (change)="onVariableChange(varName, $event)"
                  (focus)="onFieldFocus(varName)"
                  (blur)="onFieldBlur()"
                >
                  <option value="">-- Select --</option>
                  @for (option of getDropdownOptions(varName); track option.value) {
                    <option [value]="option.value">{{ option.label }}</option>
                  }
                  @if (isCustomValue(varName)) {
                    <option [value]="getVariableValue(varName)" selected>Custom: {{ getVariableValue(varName) }}</option>
                  }
                </select>
                <!-- Hard value input below dropdown -->
                <input
                  type="text"
                  [value]="getVariableValue(varName)"
                  (input)="onVariableChange(varName, $event)"
                  (focus)="onFieldFocus(varName)"
                  (blur)="onFieldBlur()"
                  placeholder="Or enter custom value"
                  autocomplete="off"
                  spellcheck="false"
                  class="hard-value-input"
                />
              } @else if (getFieldType(varName) === 'gradient') {
                <textarea
                  [id]="'var-' + varName"
                  (input)="onVariableChange(varName, $event)"
                  (focus)="onFieldFocus(varName)"
                  (blur)="onFieldBlur()"
                  [placeholder]="varName"
                  autocomplete="off"
                  spellcheck="false"
                  class="gradient-textarea"
                  rows="2"
                >{{ getVariableValue(varName) }}</textarea>
              } @else {
                <input
                  [id]="'var-' + varName"
                  type="text"
                  [value]="getVariableValue(varName)"
                  (input)="onVariableChange(varName, $event)"
                  (focus)="onFieldFocus(varName)"
                  (blur)="onFieldBlur()"
                  [placeholder]="varName"
                  autocomplete="off"
                  spellcheck="false"
                />
              }
            </div>
          }
        }

        <!-- Download / Upload Buttons -->
        <div class="style-toggle download-section">
          <button class="download-btn" (click)="openDownloadPreview()" type="button">
            Download Theme
          </button>

          <!-- Hidden file input for upload -->
          <input
            id="themeUploadInput"
            type="file"
            accept=".json,application/json"
            class="upload-file-input"
            aria-hidden="true"
            tabindex="-1"
            (change)="onFileSelected($event)"
          />
          <button class="upload-btn" (click)="onUploadClick()" type="button" aria-label="Upload a theme JSON file">
            Upload Theme
          </button>

          @if (uploadError) {
            <span class="upload-error" role="alert">{{ uploadError }}</span>
          }
        </div>
      </div>
    }
  </div>

  <!-- ── Download Preview Panel ───────────────────────────────────────────── -->
  @if (showDownloadPanel) {
    <app-theme-download-preview
      [deltaRows]="downloadPreviewRows"
      [mode]="downloadPreviewMode"
      (modeChange)="onDownloadPreviewModeChange($event)"
      (confirm)="onConfirmDownload()"
      (cancel)="onCancelDownload()"
    ></app-theme-download-preview>
  }

  <!-- ── Upload Delta Preview Panel ────────────────────────────────────────── -->
  @if (showDeltaPanel) {
    <div class="delta-panel" role="dialog" aria-label="Theme upload preview" aria-modal="true">
      <div class="delta-header">
        <span class="delta-title">Theme Upload Preview</span>
        <button
          type="button"
          class="delta-close-btn"
          (click)="onCancelUpload()"
          aria-label="Close preview"
        >&#x2715;</button>
      </div>

      <!-- Mode toggle tabs -->
      <div class="delta-mode-tabs" role="tablist" aria-label="Preview mode">
        <button
          type="button"
          class="delta-tab"
          [class.active]="deltaMode === 'light'"
          (click)="onDeltaModeChange('light')"
          role="tab"
          [attr.aria-selected]="deltaMode === 'light'"
        >Light</button>
        <button
          type="button"
          class="delta-tab"
          [class.active]="deltaMode === 'dark'"
          (click)="onDeltaModeChange('dark')"
          role="tab"
          [attr.aria-selected]="deltaMode === 'dark'"
        >Dark</button>
      </div>

      <!-- Delta table -->
      <div class="delta-table-wrap">
        @if (deltaRows.length === 0) {
          <p class="delta-empty">No variables found in <strong>{{ deltaMode }}</strong> mode.</p>
        } @else {
          <table class="delta-table" aria-label="Theme variable differences">
            <caption class="sr-only">Comparison of current theme and uploaded theme in {{ deltaMode }} mode</caption>
            <thead>
              <tr>
                <th scope="col">Key</th>
                <th scope="col">Current Json Theme</th>
                <th scope="col">Updated Theme</th>
                <th scope="col">Changes</th>
              </tr>
            </thead>
            <tbody>
              @for (row of deltaRows; track row.variable) {
                <tr
                  [class.has-change]="row.hasChange"
                  [class.is-new]="row.isNew"
                  [class.is-removed]="row.isRemoved"
                >
                  <!-- Key -->
                  <td class="delta-var-name" [title]="row.variable">{{ row.label }}</td>

                  <!-- Current Json Theme (empty when variable is new) -->
                  <td class="delta-value delta-current">
                    @if (row.before) {
                      <span class="value-chip current-chip" [title]="row.before">
                        @if (isColor(row.before)) {
                          <span class="color-dot" [style.background]="row.before" aria-hidden="true"></span>
                        }
                        {{ row.before }}
                      </span>
                    }
                  </td>

                  <!-- Updated Theme (empty when variable is removed) -->
                  <td class="delta-value delta-updated">
                    @if (row.after) {
                      <span class="value-chip updated-chip" [title]="row.after">
                        @if (isColor(row.after)) {
                          <span class="color-dot" [style.background]="row.after" aria-hidden="true"></span>
                        }
                        {{ row.after }}
                      </span>
                    }
                  </td>

                  <!-- Changes (only populated when value differs) -->
                  <td class="delta-value delta-changes">
                    @if (row.hasChange && row.changes) {
                      <span class="value-chip changes-chip" [title]="row.changes">
                        @if (isColor(row.changes)) {
                          <span class="color-dot" [style.background]="row.changes" aria-hidden="true"></span>
                        }
                        {{ row.changes }}
                      </span>
                    }
                  </td>
                </tr>
              }
            </tbody>
          </table>
        }
      </div>

      <!-- Action buttons -->
      <div class="delta-actions">
        <button
          type="button"
          class="action-btn confirm-btn"
          (click)="onConfirmUpload()"
        >Apply Theme</button>
        <button
          type="button"
          class="action-btn cancel-btn"
          (click)="onCancelUpload()"
        >Cancel</button>
      </div>
    </div>
  }
}
